// eslint configured: rule not present in CRA build
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { client, flushQueue } from "@/lib/api";
import { identify, track, resetAnalytics, EVENTS } from "@/lib/analytics";

const AuthContext = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionStartRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const r = await client.get("/auth/me");
      setUser(r.data);
      identify(r.data);
      if (!sessionStartRef.current) sessionStartRef.current = Date.now();
      flushQueue();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Log session duration on unload (only if signed in)
  useEffect(() => {
    const handler = () => {
      if (!sessionStartRef.current) return;
      const seconds = Math.round((Date.now() - sessionStartRef.current) / 1000);
      track(EVENTS.SESSION_DURATION, { seconds });
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const logout = async () => {
    try {
      await client.post("/auth/logout");
    } catch {
      // ignore logout network errors; we always clear local state below
    }
    localStorage.removeItem("session_token");
    resetAnalytics();
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh: checkAuth, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
